import { useEffect } from 'react'
import { Archive, EyeOff, FolderOpen, Rows3, X } from 'lucide-react'
import clsx from 'clsx'
import type { TableColumn } from '@/services/dbConsole'

function cell(value: unknown): string {
  if (value === null || value === undefined) return '∅'
  if (value === '') return '""'
  if (typeof value === 'object') return JSON.stringify(value, null, 1)
  return String(value)
}

/**
 * One stored row, every column, exactly as the database holds it.
 *
 * Opened by double-click or the row's Open control wherever a row has no
 * richer form to open in: an intake package (the Intake screen has no route to
 * a single record), an archived deal, a deal in another environment, or any
 * other table. When a form does exist for the row, `action` offers it.
 */
export function RowDetailDrawer({
  tableLabel,
  table,
  columns,
  row,
  archived,
  note,
  action,
  onClose,
}: {
  tableLabel: string
  table: string
  columns: TableColumn[]
  row: Record<string, unknown>
  archived: boolean
  /** Why the row opens here rather than in its own form, when it has one. */
  note: string | null
  action: { label: string; onClick: () => void } | null
  onClose: () => void
}) {
  useEffect(() => {
    // Capture phase, so this runs before a modal's own Escape handler has
    // closed it: a modal on top (the deal file, the export dialog) owns that
    // keypress, and one Escape must not close the modal and this drawer both.
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return
      onClose()
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [onClose])

  const primary = columns.filter((column) => column.primaryKey)
  const identity = primary.length
    ? primary.map((column) => cell(row[column.name])).join(' · ')
    : cell(row[columns[0]?.name ?? ''])

  return (
    <aside
      aria-label={`Stored row — ${tableLabel} ${identity}`}
      className="flex w-[360px] flex-shrink-0 flex-col overflow-hidden rounded-lg border border-amber-300/15 bg-[#0a0a0a]"
    >
      <header className="flex items-start justify-between gap-2 border-b border-amber-300/10 px-3 py-2.5">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.16em] text-amber-200/70">
            <Rows3 size={11} /> Stored row
          </div>
          <h2 className="mt-0.5 truncate text-[13px] font-semibold text-stone-100">{tableLabel}</h2>
          <div className="flex items-center gap-1.5 truncate font-mono text-[10px] text-stone-500">
            <span className="truncate">
              {table} · {identity}
            </span>
            {archived && (
              <span className="flex flex-shrink-0 items-center gap-1 rounded bg-stone-500/15 px-1.5 py-0.5 text-[8px] uppercase tracking-wider text-stone-300 ring-1 ring-inset ring-stone-400/25">
                <Archive size={8} /> Archived
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close stored row"
          className="rounded p-1 text-stone-500 transition hover:text-amber-200"
        >
          <X size={13} />
        </button>
      </header>

      {(note || action) && (
        <div className="space-y-1.5 border-b border-amber-300/[0.07] px-3 py-2">
          {note && <p className="text-[10px] leading-relaxed text-stone-400">{note}</p>}
          {action && (
            <button
              type="button"
              onClick={action.onClick}
              className="flex w-full items-center justify-center gap-1.5 rounded border border-amber-400/35 px-3 py-1.5 text-[11px] font-medium text-amber-300 transition-colors hover:bg-amber-400/10"
            >
              <FolderOpen size={12} />
              {action.label}
            </button>
          )}
        </div>
      )}

      <dl className="min-h-0 flex-1 divide-y divide-white/[0.04] overflow-y-auto">
        {columns.map((column) => {
          const value = row[column.name]
          return (
            <div key={column.name} className="px-3 py-1.5">
              <dt className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.08em] text-stone-600">
                {column.name}
                {column.redacted && <EyeOff size={8} className="text-amber-400/60" />}
              </dt>
              <dd
                className={clsx(
                  'whitespace-pre-wrap break-words font-mono text-[10px]',
                  value === '[redacted]'
                    ? 'text-amber-300/70'
                    : value === null || value === undefined
                      ? 'text-stone-700'
                      : 'text-stone-200',
                )}
              >
                {cell(value)}
              </dd>
            </div>
          )
        })}
      </dl>
    </aside>
  )
}
